/**
 * Canonical expense category → subcategory taxonomy. **Single source
 * of truth** for the whole app — both web and mobile (and the
 * forthcoming native LLM) read from here. Every downstream consumer
 * (per-category detector whitelists, keyword files, icon mapping,
 * i18n labels) is *derived* from this map, not duplicated.
 *
 * Keys match the lucide icons assigned in
 * `@splitfiesta/shared/expense-subcategory-icons.ts` and the runtime
 * subcategory files in `apps/web/src/utils/<cat>Subcategories.ts`,
 * which web and mobile have been shipping aligned on for months.
 * Cross-category key overlap is allowed and intentional (`groceries`
 * in both food and shopping, `water` in utilities/entertainment,
 * `rent` in utilities/accommodation, etc.) — the detector and LLM
 * both return `(category, subcategory)` together, so context
 * disambiguates.
 */
import type { ExpenseCategory } from './types';

export const EXPENSE_TAXONOMY = {
  food: [
    'pizza',
    'hamburger',
    'sandwich',
    'soup',
    'cafe',
    'bakery',
    'groceries',
    'snacks',
    'breakfast',
    'dessert',
    'fruit',
    'vegetables',
    'meat',
    'seafood',
    'dairy',
    'steak',
    'delivery',
    'salad',
    'sushi',
    'restaurant',
    'icecream',
    'candy',
    'pastry',
    'supplements',
  ],
  drinks: [
    'drinks',
    'water',
    'juice',
    'soda',
    'coffee',
    'tea',
    'energy',
    'beer',
    'wine',
    'cocktails',
    'spirits',
    'alcohol',
    'bar',
  ],
  transport: [
    'car',
    'bus',
    'train',
    'tram',
    'flight',
    'bike',
    'boat',
    'parking',
    'taxi',
    'rideshare',
    'metro',
    'fuel',
    'toll',
    'rental',
    'maintenance',
    'airport',
  ],
  shopping: [
    'clothes',
    'electronics',
    'groceries',
    'online',
    'furniture',
    'gifts',
    'services',
    'shoes',
    'books',
    'toys',
    'mall',
    'market',
    'department',
    'jewelry',
    'cosmetics',
    'sports',
    'hardware',
    'garden',
  ],
  healthcare: [
    'doctor',
    'pharmacy',
    'hospital',
    'dental',
    'vision',
    'specialist',
    'insurance',
    'fitness',
    'supplements',
  ],
  utilities: [
    'electricity',
    'water',
    'gas',
    'internet',
    'phone',
    'trash',
    'maintenance',
    'streaming',
  ],
  entertainment: [
    'music',
    'streaming',
    'gaming',
    'books',
    'fitness',
    'events',
    'sports',
    'photography',
    'podcasts',
    'outdoor',
    'water',
    'movies',
  ],
  accommodation: [
    'hotel',
    'airbnb',
    'apartment',
    'house',
    'rent',
    'hostel',
    'camping',
  ],
  education: [],
  other: [],
} as const satisfies Readonly<Record<ExpenseCategory, readonly string[]>>;

/**
 * Bumped manually whenever `EXPENSE_TAXONOMY` itself changes
 * (subcategories added / removed / renamed). The cloud-LLM cache
 * stamps every entry with the version it was generated against and
 * treats version drift as a cache miss, so a single integer bump
 * here invalidates every stale row without a migration script.
 *
 * Downstream consumers compare with `!==`, not `>=`.
 *
 * 2: `supplements` added under food and healthcare (2026-09-20).
 * 3: drinks promoted to a top-level category (2026-09-22).
 */
export const EXPENSE_TAXONOMY_VERSION = 3;

/**
 * Type of a valid subcategory key for a given category. Doesn't
 * enforce cross-category uniqueness — see the note about
 * `groceries`/`water`/`rent` etc. living in multiple parents.
 */
export type ExpenseSubcategory<C extends ExpenseCategory = ExpenseCategory> =
  (typeof EXPENSE_TAXONOMY)[C][number];

/**
 * Validate that a `(category, subcategory)` pair belongs to the
 * taxonomy. Used to gate LLM output before it's surfaced to the UI —
 * a hallucinated `{ category: 'food', subcategory: 'gas' }` is
 * rejected here and the caller falls back to the keyword detector.
 *
 * `subcategory` is optional — predictions without one are valid as
 * long as the category itself is.
 */
export function isValidExpensePrediction(
  category: string,
  subcategory?: string | null
): category is ExpenseCategory {
  // Own-property check, not a truthiness one: an LLM that answered
  // `constructor` would otherwise reach Object.prototype's and validate (or,
  // with a subcategory, throw on a function that has no `includes`).
  if (!Object.hasOwn(EXPENSE_TAXONOMY, category)) return false;
  const subs = EXPENSE_TAXONOMY[category as ExpenseCategory];
  if (!subcategory) return true;
  return (subs as readonly string[]).includes(subcategory);
}

/**
 * Helper for constructing the LLM prompt. Returns one line per
 * category in the form `food: pizza, hamburger, sandwich, …` so the
 * model sees both the category labels and the legal subcategory
 * keys at once. Categories without subcategories (education, other)
 * show just the category name.
 */
export function describeExpenseTaxonomy(): string {
  return (Object.keys(EXPENSE_TAXONOMY) as ExpenseCategory[])
    .map(cat => {
      const subs = EXPENSE_TAXONOMY[cat];
      return subs.length === 0 ? cat : `${cat}: ${subs.join(', ')}`;
    })
    .join('\n');
}
